import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { call, get } from '@swf/core/api/embedded';
import { StatusCode } from '@swf/core/enums/statusCode';
import useFromEvent from '@swf/core/hooks/useFromEvent';
import { select } from '@swf/core/store';
import type { Response } from '@swf/core/types/Account';

interface LocationState {
  extAuthToken?: string;
  error?: string;
}

const useMitId = <T extends { extAuthToken?: string }>(
  effect?: (values: T) => Promise<Response>
) => {
  const isEmbedded = useSelector(select.site.isEmbedded);
  const fromEvent = useFromEvent();
  const { state: locationState } = useLocation<LocationState>();
  const [loading, setLoading] = useState(false);
  const [mitIdData, setMitIdData] = useState<T>();
  const [formValues, setFormValues] = useState<T>();
  const [isMitIdRequired, setIsMitIdRequired] = useState(false);
  const [mitIdError, setMitIdError] = useState(false);

  const handleError = useCallback(
    (values: T) => {
      setFormValues(values);
      if (isEmbedded) {
        call(['mitid', 'requestToken']);
      } else {
        setIsMitIdRequired(true);
      }
    },
    [isEmbedded]
  );

  const mitIdSubmit = async (values: T) => {
    setLoading(true);
    const response = (await effect?.(values)) as Response;
    setLoading(false);

    if (response.errors?.code === StatusCode.MITID_AUTH_REQUIRED) {
      handleError(values);
      return;
    }

    setMitIdData(undefined);

    return response;
  };

  const starsMitIdToken = useCallback(
    () =>
      setMitIdData({
        extAuthToken: get(['mitid', 'token']) as string,
        ...formValues
      } as T),
    [formValues]
  );

  useEffect(() => {
    if (locationState?.error) {
      setMitIdError(true);
    } else if (locationState?.extAuthToken) {
      setMitIdData(locationState as T);
    }
  }, [locationState, mitIdError]);

  useEffect(() => {
    if (isEmbedded) {
      fromEvent(document, 'starsmitidtoken').subscribe(() => starsMitIdToken());
    }
  }, [fromEvent, isEmbedded, starsMitIdToken]);

  return { loading, mitIdError, mitIdData, isMitIdRequired, mitIdSubmit };
};

export default useMitId;
